/**
 * How one ticket can be bound to another.
 *
 * EVERY LINK IS STORED ONCE, IN ONE DIRECTION, and read from both ends. The
 * alternative — a row each way — is the same fact written twice, and two
 * copies of a fact eventually disagree: an unlink that removes one row leaves
 * a ticket whose sibling still claims the relationship.
 *
 * So each kind carries the words for both ends. `parent_of` reads "Parent of"
 * looking down and "Child of" looking up; `related` reads the same either
 * way, which is what makes it symmetric. The UI never has to know which end
 * of a row it is holding — it asks for the label for its direction.
 */

export const LINK_TYPES = [
  /** This ticket is the request; the other is a piece of work it became. */
  'parent_of',
  /** Two tickets worth reading together. No hierarchy implied. */
  'related',
  /** The other is the same request, arrived twice. */
  'duplicate_of',
  /** Closed into the other; written by the merge path, never by hand. */
  'merged_into',
  /** Moved to another desk; written by the transfer path, never by hand. */
  'transferred_to',
  /**
   * A reply to a CLOSED ticket, filed as a new one (063). From the new ticket
   * to the old; written by intake, never by hand.
   */
  'follow_up_of',
] as const;

export type LinkType = (typeof LINK_TYPES)[number];

export interface LinkKind {
  /** How it reads from the row's `from` end. */
  forward: string;
  /** How it reads from the row's `to` end. */
  reverse: string;
  /** Same word both ways, so direction carries no meaning. */
  symmetric: boolean;
  /**
   * Whether a person may create or remove it.
   *
   * `merged_into` and `transferred_to` are records of something that HAPPENED
   * — a ticket was cancelled into another, a ticket left the desk. Letting
   * somebody add one by hand would let them assert a history that never
   * occurred, and removing one would erase where a ticket went.
   */
  manual: boolean;
  /** One line explaining when to reach for it, shown in the picker. */
  hint: string;
}

export const LINK_KINDS: Readonly<Record<LinkType, LinkKind>> = {
  parent_of: {
    forward: 'Parent of',
    reverse: 'Child of',
    symmetric: false,
    manual: true,
    hint: 'One request that became several pieces of work — onboarding into accounts and hardware.',
  },
  related: {
    forward: 'Related to',
    reverse: 'Related to',
    symmetric: true,
    manual: true,
    hint: 'Worth reading together, with neither one part of the other.',
  },
  duplicate_of: {
    forward: 'Duplicate of',
    reverse: 'Duplicated by',
    symmetric: false,
    manual: true,
    hint: 'The same request arrived twice. Use merge instead if one should be closed.',
  },
  merged_into: {
    forward: 'Merged into',
    reverse: 'Merged from',
    symmetric: false,
    manual: false,
    hint: 'Recorded when a ticket is merged.',
  },
  transferred_to: {
    forward: 'Transferred to',
    reverse: 'Transferred from',
    symmetric: false,
    manual: false,
    hint: 'Recorded when a ticket moves to another desk.',
  },
  follow_up_of: {
    forward: 'Follow-up of',
    reverse: 'Followed up by',
    symmetric: false,
    manual: false,
    hint: 'Recorded when someone replies to a closed ticket: the reply becomes a new ticket.',
  },
};

/** The kinds a person may pick in the link dialog. */
export const MANUAL_LINK_TYPES: readonly LinkType[] = LINK_TYPES.filter(
  (t) => LINK_KINDS[t].manual,
);

/**
 * What this link reads as from the ticket doing the looking.
 *
 * `outgoing` means the reader is the row's `from` end.
 */
export function linkLabel(type: LinkType, outgoing: boolean): string {
  const k = LINK_KINDS[type];
  return outgoing ? k.forward : k.reverse;
}

export function isManualLink(type: string): type is LinkType {
  return (MANUAL_LINK_TYPES as readonly string[]).includes(type);
}
